import {Box, Button, Checkbox, FormControl, Link, Text} from '@primer/react'
import type {DialogProps} from '@primer/react/experimental'

import {BUTTON_LABELS, HOTKEYS, LABELS, TEST_IDS} from './constants'
import {useIssueCreateContext} from './contexts/IssueCreateContext'
import {useKeyPress} from '@github-ui/use-key-press'
import {issuePath} from '@github-ui/paths'

type CreateIssueFooterProps = {
  onClose: () => void
} & DialogProps

export const CreateIssueFooter = ({sx, onClose}: CreateIssueFooterProps) => {
  const {createMore, setCreateMore, createMoreCreatedPath, onCreateAction, isSubmitting} = useIssueCreateContext()

  const handleCheckMoreChange = () => {
    setCreateMore(!createMore)
  }
  useKeyPress(
    [HOTKEYS.enter],
    () => {
      onCreateAction?.current?.onCreate(isSubmitting, createMore)
    },
    {metaKey: true, triggerWhenInputElementHasFocus: true, triggerWhenPortalIsActive: true},
  )

  useKeyPress(
    [HOTKEYS.enter],
    () => {
      setCreateMore(true)
      onCreateAction?.current?.onCreate(isSubmitting, true)
    },
    {metaKey: true, altKey: true, triggerWhenInputElementHasFocus: true, triggerWhenPortalIsActive: true},
  )
  return (
    <Box sx={{width: '100%', display: 'flex', justifyContent: 'space-between', ...sx}}>
      <Box sx={{display: 'flex', gap: 2, alignItems: 'center'}}>
        <FormControl sx={{display: 'flex', alignItems: 'center', '> :first-child': {display: 'contents'}}}>
          <Checkbox checked={createMore} onChange={handleCheckMoreChange} />
          <FormControl.Label>{BUTTON_LABELS.createMore}</FormControl.Label>
        </FormControl>
        <Box
          data-testid={TEST_IDS.issueCreatedAnnouncement}
          className="sr-only"
          aria-live="polite"
          aria-atomic="true"
          role="status"
        >
          {createMoreCreatedPath.number && LABELS.lastIssueCreated(createMoreCreatedPath.number)}
        </Box>
        {createMoreCreatedPath.number && (
          <>
            <Text sx={{color: 'fg.muted'}}>·</Text>
            <Link
              href={issuePath({
                owner: createMoreCreatedPath.owner,
                repo: createMoreCreatedPath.repo,
                issueNumber: createMoreCreatedPath.number,
              })}
              data-testid={TEST_IDS.issueCreatedLink}
            >
              {LABELS.lastIssueCreated(createMoreCreatedPath.number)}
            </Link>
          </>
        )}
      </Box>
      <Box sx={{display: 'flex', alignItems: 'center', gap: 2}}>
        <Button onClick={() => onClose()}>{BUTTON_LABELS.cancel}</Button>
        <Button
          variant="primary"
          disabled={isSubmitting}
          onClick={() => onCreateAction?.current?.onCreate(isSubmitting, createMore)}
          trailingVisual={() => (
            <Box
              sx={{
                borderWidth: 1,
                borderStyle: 'solid',
                p: '0px 4px',
                borderRadius: '6px',
                borderColor: 'border.subtle',
                color: 'fg.onEmphasis',
                fontSize: '11px',
              }}
            >
              {HOTKEYS.commandSymbol}&nbsp;{HOTKEYS.enterSymbol}
            </Box>
          )}
        >
          {BUTTON_LABELS.create}
        </Button>
      </Box>
    </Box>
  )
}

try{ CreateIssueFooter.displayName ||= 'CreateIssueFooter' } catch {}